import { motion } from "framer-motion";
import ParticleHero from "./ParticleHero";
import { WEBINAR_DATE, WEBINAR_TIME, HEADSHOT_URL } from "./shared";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
});

const BONUSES = [
  "Live seat in the VA Better-Buy Masterclass™",
  "Live Q&A + whole-deal comparison walkthrough",
  "Live explanation of the application path: Prepare / Apply / Review",
  "Optional one-on-one Better Deal Review™ after you complete a mortgage application",
  "A verbal assumptions & next-step recap on your review conversation",
];

export default function Hero() {
  return (
    <section className="relative min-h-screen bg-grid-80 overflow-hidden" data-testid="hero-section">
      <div className="absolute right-0 top-0 h-full w-full lg:w-1/2">
        <ParticleHero />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#001736] via-[#001736]/70 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-32 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="font-mono-strict text-[10px] tracking-[0.25em] text-white/30 mb-8" data-testid="hero-datetime">
            📅 {WEBINAR_DATE} AT {WEBINAR_TIME}
          </div>
          <p className="font-mono-strict text-[10px] tracking-[0.28em] text-[#0162CA] mb-6">
            FREE LIVE MASTERCLASS · VA-BACKED HOME FINANCING · FLORIDA &amp; PENNSYLVANIA
          </p>
          <h1
            className="font-black uppercase leading-[0.95]"
            style={{ fontSize: "clamp(40px, 6vw, 76px)", letterSpacing: "-0.03em" }}
            data-testid="hero-headline"
          >
            <span className="block overflow-hidden">
              <motion.span className="block" {...lineAnim(0)}>A VA HOME PURCHASE</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block text-[#0162CA]" {...lineAnim(1)}>IS A WHOLE DEAL—</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block opacity-60" style={{ fontSize: "62%" }} {...lineAnim(2)}>
                NOT JUST A RATE QUOTE
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mt-7 text-[15px] text-white/55 leading-relaxed max-w-xl">
              A free live masterclass on how to <span className="text-[#0162CA] font-semibold">clarify</span>,{" "}
              <span className="text-[#0162CA] font-semibold">structure</span>, and confidently advance a VA-backed
              home purchase — so you understand the whole transaction before you commit to anything.
            </p>
            <blockquote className="mt-6 border-l-2 border-[#0162CA] pl-4 italic text-sm text-white/60 max-w-lg">
              What if the reason the numbers never made sense wasn't your file — it was that nobody ever structured
              the whole deal?
            </blockquote>
            <div className="mt-8">
              <button
                type="button"
                data-cursor="cta"
                data-testid="hero-cta-button"
                onClick={() => {}}
                className="cta-btn"
              >
                ✓ RESERVE MY FREE SEAT
              </button>
              <p className="mt-3 text-[11px] text-white/25">
                Free to attend · Seating for the live session is limited
              </p>
            </div>
            <div className="mt-10 flex items-center gap-4" data-testid="hero-host-module">
              <img
                src={HEADSHOT_URL}
                alt="Josh Bartlett"
                className="h-12 w-12 rounded-full object-cover border-2 border-[#0162CA]"
              />
              <div>
                <p className="text-sm font-bold">Josh Bartlett</p>
                <p className="text-xs text-white/45">
                  Branch Manager · Shoreline Mortgage Group · NMLS# 1425493
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          id="hero-form"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="border border-white/12 bg-[#00224F]/55 backdrop-blur-xl p-6 sm:p-8" data-testid="hero-registration-card">
            <p className="font-mono-strict text-[11px] tracking-[0.3em] text-[#0162CA] mb-4">SAVE YOUR SEAT</p>
            <h3 className="font-black uppercase text-lg mb-4 tracking-tight">
              When You Register, You'll Get Access To:
            </h3>
            <ul className="space-y-2 mb-5">
              {BONUSES.map((b) => (
                <li key={b} className="flex gap-2.5 text-[13px] text-white/70 leading-snug">
                  <span className="text-[#0162CA] font-bold shrink-0">✓</span>
                  {b}
                </li>
              ))}
            </ul>
            <p className="text-[#0162CA] font-black text-[22px]">FREE!</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
